import { motion, useReducedMotion } from 'framer-motion';
import type { Transition } from 'framer-motion';

export interface BlobsProps {
  className?: string;
}

type Blob = {
  className: string;
  background: string;
  animate: { x: number[]; y: number[]; scale: number[] };
  duration: number;
};

// Sumi-ink (primary) + cinnabar only, low opacity — mirrors the .washi
// radial gradients. No new hues.
const BLOBS: Blob[] = [
  {
    className: '-top-32 -left-24 h-96 w-96',
    background: 'radial-gradient(circle, hsl(219 55% 27% / 0.16), transparent 65%)',
    animate: { x: [0, 14, 0], y: [0, 20, 0], scale: [1, 1.06, 1] },
    duration: 13,
  },
  {
    className: 'top-1/3 -right-24 h-[28rem] w-[28rem]',
    background: 'radial-gradient(circle, hsl(351 78% 43% / 0.12), transparent 65%)',
    animate: { x: [0, -12, 0], y: [0, -22, 0], scale: [1, 1.05, 1] },
    duration: 14,
  },
  {
    className: 'bottom-[-6rem] left-1/3 h-80 w-80',
    background: 'radial-gradient(circle, hsl(219 55% 27% / 0.1), transparent 65%)',
    animate: { x: [0, -10, 0], y: [0, 12, 0], scale: [1, 1.04, 1] },
    duration: 11,
  },
];

const loop = (duration: number): Transition => ({
  duration,
  repeat: Infinity,
  ease: 'easeInOut',
});

/**
 * Decorative drifting washi-gradient blobs for cinematic sections (hero).
 * Absolutely positioned, behind content (-z-10), pointer-events-none,
 * aria-hidden — never affects layout. Under prefers-reduced-motion the
 * blobs are rendered static (no animate).
 */
export function Blobs({ className }: BlobsProps) {
  const reduced = useReducedMotion();

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${className ?? ''}`}
    >
      {BLOBS.map((blob, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full blur-3xl ${blob.className}`}
          style={{ background: blob.background }}
          {...(reduced ? {} : { animate: blob.animate, transition: loop(blob.duration) })}
        />
      ))}
    </div>
  );
}
